import { useMemo } from "react";
import { useGlobalData } from "./useGlobalHooks";

/**
 * Calculate total multiplier for a user
 */
function calculateTotalMultiplier(userMapping) {
  return (
    (userMapping.index_multiplier || 0) +
    (userMapping.banknifty_multiplier || 0) +
    (userMapping.finnifty_multiplier || 0) +
    (userMapping.midcpnifty_multiplier || 0) +
    (userMapping.sensex_multiplier || 0) +
    (userMapping.stock_multiplier || 0)
  );
}

/**
 * Calculate total size (quantity) for a client across all positions
 */
function calculateTotalSize(positions, clientId) {
  if (!Array.isArray(positions)) return 0;
  return positions
    .filter((p) => p.client_id === clientId)
    .reduce((sum, p) => sum + (p.qty || 0), 0);
}

/**
 * Calculate open value for a client
 * Open Value = Sum of (avg_price * qty) for all positions
 */
function calculateOpenValue(positions, clientId) {
  if (!Array.isArray(positions)) return 0;
  return positions
    .filter((p) => p.client_id === clientId)
    .reduce((sum, p) => sum + (p.avg_price || 0) * (p.qty || 0), 0);
}

/**
 * Calculate cash available
 * Cash Available = Balance - Open Value
 */
function calculateCashAvailable(balance, openValue) {
  return balance - openValue;
}

/**
 * Hook for Account View data
 * Combines data from multiple sources to create account overview
 *
 * @returns {Object} { data, loading, error }
 */
export function useAccountView() {
  const {
    positions,
    userMappings,
    users,
    indexMultipliers,
    indexMultipliersRaw,
    loading,
  } = useGlobalData();

  const accountViewData = useMemo(() => {
    // Only wait for essential data (not positions for initial render)
    if (loading.userMappings || loading.users || loading.indexMultipliers) {

      return [];
    }
    // console.log("all Users Here---mmmm-----",users);
    // Validate data exists and is array
    if (!Array.isArray(userMappings)) {
      console.log("userMappings is not an array:", userMappings);
      return [];
    }

    if (!Array.isArray(users)) {
      console.log("users is not an array:", users);
      return [];
    }

    // Positions can be empty initially (will update when available)
    const positionsArray = Array.isArray(positions) ? positions : [];

    // console.log("📊 Building account view:", {
    //   userMappings: userMappings.length,
    //   users: users.length,
    //   positions: positionsArray.length,
    //   positionsLoaded: !loading.positions,
    //   ordersLoaded: !loading.orders,
    //   timestamp: new Date().toISOString(),
    // });

    // console.log("🚀 ACCOUNT VIEW DATA READY - Should render NOW!");
    performance.mark("account-view-data-ready");

    // Create a combined list of all users from both userMappings and users arrays
    // Use composite key (client_id + broker) to handle same client_id with different brokers
    const allUsers = [];
    const seenKeys = new Set();

    // Add from userMappings (keep original broker names)
    userMappings.forEach((um) => {
      const key = `${um.client_id}_${um.broker || 'unknown'}`;
      if (!seenKeys.has(key)) {
        seenKeys.add(key);
        allUsers.push({ client_id: um.client_id, broker: um.broker });
      }
    });

    // Add from users (keep original broker names)
    users.forEach((u) => {
      const key = `${u.client_id}_${u.broker || 'unknown'}`;
      if (!seenKeys.has(key)) {
        seenKeys.add(key);
        allUsers.push({ client_id: u.client_id, broker: u.broker });
      }
    });

    // Build account view data for all users
    const data = allUsers
      .map(({ client_id: clientId, broker }) => {
        // Find corresponding entries with flexible broker matching
        // Try exact match first, then try normalized versions
        let userMapping = userMappings.find(
          (um) => um.client_id === clientId && um.broker === broker
        );

        // If not found and broker is dhan_anurag, try dhan_ss_anurag
        if (!userMapping && broker === 'dhan_anurag') {
          userMapping = userMappings.find(
            (um) => um.client_id === clientId && um.broker === 'dhan_ss_anurag'
          );
        }

        // If not found and broker is dhan_ss_anurag, try dhan_anurag
        if (!userMapping && broker === 'dhan_ss_anurag') {
          userMapping = userMappings.find(
            (um) => um.client_id === clientId && um.broker === 'dhan_anurag'
          );
        }


        const user = users.find((u) => u.client_id === clientId && u.broker === broker);

        // Log user data for debugging
        // if (userMapping.client_id) {
        //   console.log(`👤 User ${userMapping.client_id}:`, {
        //     name: user?.name,
        //     cash_input: user?.cash_input,
        //     discount: user?.discount,
        //     full_user: user,
        //   });
        // }

        // Find index multiplier data (safely handle if not array)
        // Find index multiplier data (raw data contains mcx_multiplier and other fields)
        // Use flexible broker matching
        let indexMul = Array.isArray(indexMultipliers)
          ? indexMultipliers.find((im) => im.client_id === clientId && im.broker === broker)
          : null;

        if (!indexMul && broker === 'dhan_anurag') {
          indexMul = Array.isArray(indexMultipliers)
            ? indexMultipliers.find((im) => im.client_id === clientId && im.broker === 'dhan_ss_anurag')
            : null;
        }

        if (!indexMul && broker === 'dhan_ss_anurag') {
          indexMul = Array.isArray(indexMultipliers)
            ? indexMultipliers.find((im) => im.client_id === clientId && im.broker === 'dhan_anurag')
            : null;
        }

        let indexMulRaw = Array.isArray(indexMultipliersRaw)
          ? indexMultipliersRaw.find((im) => im.client_id === clientId && im.broker === broker)
          : null;

        if (!indexMulRaw && broker === 'dhan_anurag') {
          indexMulRaw = Array.isArray(indexMultipliersRaw)
            ? indexMultipliersRaw.find((im) => im.client_id === clientId && im.broker === 'dhan_ss_anurag')
            : null;
        }

        if (!indexMulRaw && broker === 'dhan_ss_anurag') {
          indexMulRaw = Array.isArray(indexMultipliersRaw)
            ? indexMultipliersRaw.find((im) => im.client_id === clientId && im.broker === 'dhan_anurag')
            : null;
        }

        // Get balance
        const balance = user?.balance || 0;

        // Calculate multipliers - use userMapping if available, otherwise try user data or default to 0
        const niftyMul =
          userMapping?.index_multiplier ||
          userMapping?.multiplier ||
          indexMulRaw?.multiplier ||
          0;
        const bnfMul =
          userMapping?.banknifty_multiplier ||
          indexMulRaw?.banknifty_multiplier ||
          0;
        const finniftyMul =
          userMapping?.finnifty_multiplier ||
          indexMulRaw?.finnifty_multiplier ||
          0;
        const midcpniftyMul =
          userMapping?.midcpnifty_multiplier ||
          indexMulRaw?.midcpnifty_multiplier ||
          0;
        const sensexMul =
          userMapping?.sensex_multiplier || indexMulRaw?.sensex_multiplier || 0;
        const stockMul =
          userMapping?.stock_multiplier || indexMulRaw?.stock_multiplier || 0;
        // MCX multiplier: prefer explicit field on userMapping, then raw index multipliers API, then user record
        const mcxMul =
          userMapping?.mcx_multiplier ||
          (indexMulRaw && indexMulRaw.mcx_multiplier) ||
          user?.mcx_multiplier ||
          0;
        const totalMul =
          niftyMul +
          bnfMul +
          finniftyMul +
          midcpniftyMul +
          sensexMul +
          stockMul +
          mcxMul;

        // Calculate totals (use positionsArray instead of positions)
        // Filter positions by BOTH client_id AND broker
        const totalSize = positionsArray
          .filter((p) => p.client_id === clientId && p.broker === broker)
          .reduce((sum, p) => sum + (p.qty || 0), 0);
        const openValue = positionsArray
          .filter((p) => p.client_id === clientId && p.broker === broker)
          .reduce((sum, p) => sum + (p.avg_price || 0) * (p.qty || 0), 0);
        const cashAvailable = calculateCashAvailable(balance, openValue);

        // Get cash_input and discount from user data (from API)
        const cashInput = user?.cash_input || 0;
        const discountInput = user?.discount || 0;
        const commentInput = null;
        const flag = null;
        const tokenGenerated = user?.is_token_generated ? "Yes" : "No";
        const label = user?.label;
        // Skip if we have neither user nor userMapping data
        if (!user && !userMapping) {
          return null;
        }

        return {
          name: userMapping?.name || user?.name || user?.client_id || "Unknown",
          clientId: clientId,
          broker: broker || user?.broker || userMapping?.broker || "-",
          status: userMapping?.status || user?.status || "unknown",
          cashInput,
          niftyMul,
          bnfMul,
          finniftyMul,
          midcpniftyMul,
          sensexMul,
          stockMul,
          mcxMul,
          totalMul,
          cashAvailable,
          openValue,
          totalSize,
          discountInput,
          commentInput,
          flag,
          tokenGenerated,
          label,
          proxyToggle: !!user?.proxy_toggle,
          is_pledge_plus: user?.is_pledge_plus,
          is_pledgeplus_mini: user?.is_pledgeplus_mini
        };
      })
      .filter(Boolean); // Remove null entries

    // console.log("📊 Account View processed data:", {
    //   totalUsers: data.length,
    //   userMappingsCount: userMappings.length,
    //   usersCount: users.length,
    //   brokerBreakdown: data.reduce((acc, user) => {
    //     const broker = user.broker || "unknown";
    //     acc[broker] = (acc[broker] || 0) + 1;
    //     return acc;
    //   }, {}),
    // });

    // Sort data: Active users first (alphabetically), then inactive users (alphabetically)
    data.sort((a, b) => {
      // First sort by status (active first)
      if (a.status === "active" && b.status !== "active") return -1;
      if (a.status !== "active" && b.status === "active") return 1;

      // Then sort alphabetically by name (handle potential missing names)
      const aName = a.name || a.clientId || "";
      const bName = b.name || b.clientId || "";
      return aName.localeCompare(bName);
    });

    return data;
  }, [
    positions,
    userMappings,
    users,
    indexMultipliers,
    indexMultipliersRaw,
    loading.userMappings,
    loading.users,
    loading.indexMultipliers,
  ]);

  const isLoading =
    loading.userMappings || loading.users || loading.indexMultipliers;

  return {
    data: accountViewData,
    loading: isLoading,
  };
}
